import React from 'react';
import { Link } from 'react-router-dom';
import { Lock } from 'lucide-react';
import CategoryPill from './CategoryPill';
import AuthorBadge from './AuthorBadge';

function AccessBadge({ accessLevel }) {
    if (accessLevel === 'public') {
        return (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">
                Free
            </span>
        );
    }
    if (accessLevel === 'private') {
        return (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-gray-100 text-gray-500 border border-gray-200">
                <Lock size={9} />
                Members
            </span>
        );
    }
    return null; // preview shows no badge — paywall revealed on click
}

const ArticleCard = ({ article, isFeatured = false }) => {
    if (isFeatured) {
        return (
            <Link
                to={`/research/${article.category}/${article.slug}`}
                className="group relative flex flex-col md:flex-row gap-6 bg-white p-6 rounded-3xl border border-gray-100 shadow-sm hover:shadow-xl transition-all duration-300 overflow-hidden"
            >
                <div className="md:w-1/2 overflow-hidden rounded-2xl">
                    <img
                        src={article.cover_image}
                        alt={article.title}
                        className="w-full h-72 md:h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                </div>
                <div className="md:w-1/2 flex flex-col justify-between py-2">
                    <div>
                        <div className="flex items-center gap-3 mb-4">
                            <CategoryPill category={article.category} />
                            <AccessBadge accessLevel={article.access_level} />
                            <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest flex items-center gap-1">
                                {article.author_type === 'human' ? '✍️ Human Written' : '🤖 AI Analysis'}
                            </span>
                        </div>
                        <h2 className="text-2xl md:text-3xl font-black text-[#222425] leading-tight mb-4 group-hover:text-[#00DA76] transition-colors">
                            {article.title}
                        </h2>
                        <p className="text-gray-500 line-clamp-3 mb-6 leading-relaxed">
                            {article.excerpt}
                        </p>
                    </div>

                    <div className="flex items-center justify-between mt-auto">
                        <div className="flex items-center gap-4 text-xs font-bold text-gray-400">
                            <span className="flex items-center gap-1.5">
                                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                {article.reading_time_min} min read
                            </span>
                            <span>•</span>
                            <span>{new Date(article.published_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                        </div>
                        <div className="bg-[#00DA76] text-white px-5 py-2.5 rounded-xl font-bold text-sm flex items-center gap-2 group-hover:gap-3 transition-all">
                            Read Analysis
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                            </svg>
                        </div>
                    </div>
                </div>
            </Link>
        );
    }

    return (
        <Link
            to={`/research/${article.category}/${article.slug}`}
            className="group flex flex-col bg-white rounded-3xl border border-gray-100 shadow-sm hover:shadow-lg transition-all duration-300 overflow-hidden"
        >
            <div className="relative h-48 overflow-hidden">
                <img
                    src={article.cover_image}
                    alt={article.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute top-4 left-4 flex items-center gap-2">
                    <CategoryPill category={article.category} className="shadow-lg" />
                    <AccessBadge accessLevel={article.access_level} />
                </div>
            </div>

            <div className="p-6 flex flex-col flex-1">
                <h3 className="text-lg font-black text-[#222425] leading-snug mb-3 group-hover:text-[#00DA76] transition-colors line-clamp-2">
                    {article.title}
                </h3>
                <p className="text-gray-500 text-sm line-clamp-3 mb-6 leading-relaxed">
                    {article.excerpt}
                </p>

                <div className="mt-auto flex items-center justify-between border-t border-gray-50 pt-4">
                    <div className="flex items-center gap-3 text-[10px] font-bold text-gray-400">
                        <span className="flex items-center gap-1 text-gray-300 italic">
                            {article.author_type === 'ai' ? '🤖 AI' : '✍️ Human'}
                        </span>
                        <span>•</span>
                        <span>{article.reading_time_min} min</span>
                    </div>
                    <div className="flex items-center gap-4">
                        <button className="text-gray-300 hover:text-[#00DA76] transition-colors" title="Listen">
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15L4 14.414V9.586L5.586 9H10l5-5v16l-5-5H5.586z" />
                            </svg>
                        </button>
                        <button className="text-gray-300 hover:text-[#00DA76] transition-colors" title="Ask AI">
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                            </svg>
                        </button>
                    </div>
                </div>
            </div>
        </Link>
    );
};

export default ArticleCard;
